// frontend/TwitterAnalytics/modules/IssueDetection.jsx
/**
 * Module 4: Issue Detection
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Paper, Typography, Button, Select, MenuItem,
  FormControl, InputLabel, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Chip, IconButton, Avatar,
  Dialog, DialogTitle, DialogContent, CircularProgress, Tooltip, 
  List, ListItem, ListItemText, Divider, Alert
} from '@mui/material';
import {
  Warning as UrgentIcon,
  BubbleChart as ClusterIcon,
  CheckCircle as ResolvedIcon,
  PlayArrow as InProgressIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import axiosInstance from "../../../../utils/axiosInstance";

// Simple Word Cloud Component
const WordCloud = ({ data }) => {
  if (!data || data.length === 0) {
    return <Typography color="textSecondary">No word data available</Typography>;
  }
  const maxCount = Math.max(...data.map(d => d.count));
  const minSize = 12;
  const maxSize = 48;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', p: 2 }}>
      {data.slice(0, 50).map((item, idx) => {
        const size = minSize + ((item.count / maxCount) * (maxSize - minSize));
        const opacity = 0.4 + ((item.count / maxCount) * 0.6);
        return (
          <Typography
            key={idx}
            sx={{ fontSize: size, opacity, color: 'primary.main', cursor: 'default', '&:hover': { opacity: 1 } }}
          >
            {item.word}
          </Typography>
        );
      })}
    </Box>
  );
};

const UrgencyBadge = ({ level }) => {
  const config = {
    critical: { color: 'error', label: 'Critical' },
    high: { color: 'warning', label: 'High' },
    medium: { color: 'info', label: 'Medium' },
    low: { color: 'default', label: 'Low' }
  };
  const cfg = config[level] || config.low;
  return <Chip label={cfg.label} size="small" color={cfg.color} />;
};

const StatusBadge = ({ status }) => {
  const config = {
    open: { color: 'error', icon: <UrgentIcon fontSize="small" /> },
    acknowledged: { color: 'warning', icon: null },
    in_progress: { color: 'info', icon: <InProgressIcon fontSize="small" /> },
    resolved: { color: 'success', icon: <ResolvedIcon fontSize="small" /> }
  };
  const cfg = config[status] || config.open;
  return <Chip icon={cfg.icon} label={status?.replace('_', ' ')} size="small" color={cfg.color} />;
};

const IssueDetection = ({ apiTier, onShowSnackbar }) => {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [urgentComments, setUrgentComments] = useState([]);
  const [urgentPagination, setUrgentPagination] = useState({ page: 1, per_page: 10, total: 0 });
  const [clusters, setClusters] = useState([]);
  const [clusterFilters, setClusterFilters] = useState({ status: '', urgency: '' });
  const [clustering, setClustering] = useState(false);
  const [wordCloudData, setWordCloudData] = useState([]);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [clusterDetail, setClusterDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    fetchSummary();
    fetchClusters();
    fetchWordCloud();
  }, []);

  useEffect(() => { fetchUrgentComments(); }, [urgentPagination.page]);
  useEffect(() => { fetchClusters(); }, [clusterFilters]);

  const fetchSummary = async () => {
    try {
      const res = await axiosInstance.get('/api/twitter/issues/summary');
      if (res.data.success) setSummary(res.data.data);
    } catch (err) { console.error('Failed to fetch summary'); }
  };

  const fetchUrgentComments = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/api/twitter/issues/urgent', {
        params: {
          page: urgentPagination.page, 
          per_page: urgentPagination.per_page
        }
      });
      const response = res.data;
      if (response.success) {
        setUrgentComments(response.data);
        setUrgentPagination(prev => ({ ...prev, total: response.total_urgent }));
      }
    } catch (err) { onShowSnackbar('Failed to fetch urgent comments', 'error'); }
    finally { setLoading(false); }
  };

  const fetchClusters = async () => {
    try {
      const res = await axiosInstance.get('/api/twitter/issues/clusters', {
        params: clusterFilters
      });
      if (res.data.success) setClusters(res.data.data);
    } catch (err) { console.error('Failed to fetch clusters'); }
  };

  const fetchWordCloud = async () => {
    try {
      const res = await axiosInstance.get('/api/twitter/issues/wordcloud');
      if (res.data.success) setWordCloudData(res.data.data);
    } catch (err) { console.error('Failed to fetch word cloud'); }
  };

  const handleRunClustering = async () => {
    setClustering(true);
    try {
      const res = await axiosInstance.post('/api/twitter/issues/cluster');
      const response = res.data;
      if (response.success) {
        onShowSnackbar(response.message, 'success');
        fetchClusters();
        fetchSummary();
      } else { onShowSnackbar(response.message || 'Clustering failed', 'error'); }
    } catch (err) { onShowSnackbar('Clustering failed', 'error'); }
    finally { setClustering(false); }
  };

  const openClusterDetail = async (clusterId) => {
    setSelectedCluster(clusterId);
    setDetailLoading(true);
    try {
      const res = await axiosInstance.get(`/api/twitter/issues/clusters/${clusterId}`);
      if (res.data.success) setClusterDetail(res.data.data);
    } catch (err) { onShowSnackbar('Failed to load cluster details', 'error'); }
    finally { setDetailLoading(false); }
  };

  const handleUpdateClusterStatus = async (clusterId, status) => {
    try {
      await axiosInstance.put(`/api/twitter/issues/clusters/${clusterId}`, { status });
      onShowSnackbar('Status updated', 'success');
      fetchClusters();
      if (clusterDetail) openClusterDetail(clusterId);
    } catch (err) { onShowSnackbar('Failed to update status', 'error'); }
  };

  return (
    <Box>
      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light' }}>
              <Typography variant="h4">{summary.urgent_comments_count}</Typography>
              <Typography variant="body2">Urgent Comments</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4">{summary.total_clusters}</Typography>
              <Typography variant="body2">Issue Clusters</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light' }}>
              <Typography variant="h4">
                {(summary.clusters_by_urgency?.critical || 0) + (summary.clusters_by_urgency?.high || 0)}
              </Typography>
              <Typography variant="body2">Critical/High</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
              <Typography variant="h4">{summary.clusters_by_status?.resolved || 0}</Typography>
              <Typography variant="body2">Resolved</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Top Urgent Keywords */}
      {summary?.top_urgent_keywords?.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>Top Urgent Keywords</Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            {summary.top_urgent_keywords.map((kw, idx) => (
              <Chip key={idx} label={`${kw.keyword} (${kw.count})`} color="error" variant="outlined" />
            ))}
          </Box>
        </Paper>
      )}

      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={8}>
          {/* Issue Clusters */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Issue Clusters</Typography>
              <Button
                variant="contained"
                startIcon={clustering ? <CircularProgress size={20} color="inherit" /> : <ClusterIcon />}
                onClick={handleRunClustering}
                disabled={clustering}
              >
                {clustering ? 'Clustering...' : 'Run Clustering'}
              </Button>
            </Box>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value={clusterFilters.status} label="Status"
                    onChange={(e) => setClusterFilters(prev => ({ ...prev, status: e.target.value }))}>
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="open">Open</MenuItem>
                    <MenuItem value="acknowledged">Acknowledged</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="resolved">Resolved</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Urgency</InputLabel>
                  <Select value={clusterFilters.urgency} label="Urgency"
                    onChange={(e) => setClusterFilters(prev => ({ ...prev, urgency: e.target.value }))}>
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="critical">Critical</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {clusters.length === 0 ? (
              <Alert severity="info">No issue clusters found. Run clustering to group similar issues.</Alert>
            ) : (
              <List>
                {clusters.map((cluster) => (
                  <React.Fragment key={cluster.id}>
                    <ListItem
                      secondaryAction={<IconButton onClick={() => openClusterDetail(cluster.id)}><ViewIcon /></IconButton>}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => openClusterDetail(cluster.id)}
                    >
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="subtitle1">{cluster.name}</Typography>
                            <UrgencyBadge level={cluster.urgency_level} />
                            <StatusBadge status={cluster.status} />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="textSecondary">
                              {cluster.comment_count} comments • {cluster.affected_posts_count} posts • {cluster.unique_users_count} users
                            </Typography>
                            <Box display="flex" gap={0.5} mt={0.5} flexWrap="wrap">
                              {cluster.keywords?.slice(0, 5).map((kw, idx) => (
                                <Chip key={idx} label={kw} size="small" variant="outlined" />
                              ))}
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>

          {/* Urgent Comments */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Urgent Comments</Typography>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Comment</TableCell>
                    <TableCell>Keywords</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={4} align="center"><CircularProgress /></TableCell></TableRow>
                  ) : urgentComments.length === 0 ? (
                    <TableRow><TableCell colSpan={4} align="center">No urgent comments found</TableCell></TableRow>
                  ) : (
                    urgentComments.map((comment) => (
                      <TableRow key={comment.id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Avatar src={comment.author_profile_image} sx={{ width: 24, height: 24 }} />
                            <Typography variant="caption">@{comment.author_username}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 300 }}>
                          <Typography variant="body2">{comment.text?.substring(0, 100)}...</Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5} flexWrap="wrap">
                            {comment.urgency_keywords?.map((kw, idx) => (
                              <Chip key={idx} label={kw} size="small" color="error" />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>{comment.commented_at && format(new Date(comment.commented_at), 'MMM d')}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={urgentPagination.total} page={urgentPagination.page - 1}
              onPageChange={(e, page) => setUrgentPagination(prev => ({ ...prev, page: page + 1 }))}
              rowsPerPage={urgentPagination.per_page} rowsPerPageOptions={[10]} />
          </Paper>
        </Grid>

        {/* Right Column: Word Cloud */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Word Cloud</Typography>
              <Tooltip title="Refresh"><IconButton size="small" onClick={fetchWordCloud}><RefreshIcon /></IconButton></Tooltip>
            </Box>
            <WordCloud data={wordCloudData} />
          </Paper>
        </Grid>
      </Grid>

      {/* Cluster Detail Dialog */}
      <Dialog open={!!selectedCluster} onClose={() => setSelectedCluster(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Issue Cluster Details</Typography>
            <IconButton onClick={() => setSelectedCluster(null)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : clusterDetail ? (
            <Box>
              <Box mb={3}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Typography variant="h5">{clusterDetail.cluster.name}</Typography>
                  <UrgencyBadge level={clusterDetail.cluster.urgency_level} />
                  <StatusBadge status={clusterDetail.cluster.status} />
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="textSecondary">Comments</Typography>
                    <Typography variant="h6">{clusterDetail.cluster.comment_count}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="textSecondary">Posts Affected</Typography>
                    <Typography variant="h6">{clusterDetail.cluster.affected_posts_count}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="textSecondary">Users</Typography>
                    <Typography variant="h6">{clusterDetail.cluster.unique_users_count}</Typography>
                  </Grid>
                </Grid>
                <Box mt={2}>
                  <Typography variant="body2" color="textSecondary">Keywords</Typography>
                  <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                    {clusterDetail.cluster.keywords?.map((kw, idx) => (<Chip key={idx} label={kw} size="small" />))}
                  </Box>
                </Box>
              </Box>

              <Box mb={3}>
                <Typography variant="subtitle1" gutterBottom>Update Status</Typography>
                <Box display="flex" gap={1}>
                  <Button variant={clusterDetail.cluster.status === 'acknowledged' ? 'contained' : 'outlined'} size="small"
                    onClick={() => handleUpdateClusterStatus(selectedCluster, 'acknowledged')}>Acknowledge</Button>
                  <Button variant={clusterDetail.cluster.status === 'in_progress' ? 'contained' : 'outlined'} size="small" color="info"
                    onClick={() => handleUpdateClusterStatus(selectedCluster, 'in_progress')}>In Progress</Button>
                  <Button variant={clusterDetail.cluster.status === 'resolved' ? 'contained' : 'outlined'} size="small" color="success"
                    onClick={() => handleUpdateClusterStatus(selectedCluster, 'resolved')}>Resolved</Button>
                </Box>
              </Box>

              <Typography variant="subtitle1" gutterBottom>Related Comments</Typography>
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small">
                  <TableHead><TableRow><TableCell>User</TableCell><TableCell>Comment</TableCell><TableCell>Sentiment</TableCell></TableRow></TableHead>
                  <TableBody>
                    {clusterDetail.comments?.map((comment) => (
                      <TableRow key={comment.id}>
                        <TableCell>@{comment.author_username}</TableCell>
                        <TableCell>{comment.text?.substring(0, 100)}...</TableCell>
                        <TableCell><Chip label={comment.sentiment} size="small" color={comment.sentiment === 'negative' ? 'error' : 'default'} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (<Typography>Failed to load details</Typography>)}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default IssueDetection;